import React, { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import { components } from 'react-select';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import './GroupInformationForm.scss';

const GroupAddressAutocomplete = ({ setFieldValue, values, errors, touched }) => {
    const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

    const customStyles = {
        control: (provided) => ({
            ...provided,
            height: '48px',
            borderRadius: '6px',
            borderColor: errors.streetAddress && touched.streetAddress ? '#dc3545' : '#ced4da',
            boxShadow:
                errors.streetAddress && touched.streetAddress ? '0 0 0 0.2rem rgba(220,53,69,.25)' : provided.boxShadow,
            '&:hover': {
                borderColor: errors.streetAddress && touched.streetAddress ? '#dc3545' : '#80bdff'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d'
        })
    };

    const debouncedFetch = useRef(
        debounce(async (inputValue, callback) => {
            if (!inputValue || inputValue.length < 1) {
                callback([]);
                return;
            }
            try {
                const response = await axios.get(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(inputValue)}.json`,
                    {
                        params: {
                            access_token: MAPBOX_TOKEN,
                            autocomplete: true,
                            limit: 5,
                            country: 'US'
                        }
                    }
                );
                const options = response.data.features.map((feature) => ({
                    label: feature.place_name,
                    value: feature.place_name,
                    feature
                }));
                callback(options);
            } catch (error) {
                callback([]);
            }
        }, 300)
    ).current;

    const loadOptions = (inputValue, callback) => {
        debouncedFetch(inputValue, callback);
    };

    const handleChange = (selectedOption) => {
        if (selectedOption) {
            const { feature } = selectedOption;
            const { place_name, context } = feature;
            // Update the streetAddress field
            setFieldValue('streetAddress', place_name || '');

            // Extract and update city, state, and zip code from context
            let city = '';
            let state = '';
            let zipCode = '';

            if (context) {
                context.forEach((contextItem) => {
                    if (contextItem.id.includes('place')) {
                        city = contextItem.text;
                    }
                    if (contextItem.id.includes('region')) {
                        state = contextItem.short_code.replace('US-', '');
                    }
                    if (contextItem.id.includes('postcode')) {
                        zipCode = contextItem.text;
                    }
                });
            }
            setFieldValue('city', city);
            setFieldValue('state', state);
            setFieldValue('zipCode', zipCode);
        } else {
            // Clear fields if selection is cleared
            setFieldValue('streetAddress', '');
            setFieldValue('city', '');
            setFieldValue('state', '');
            setFieldValue('zipCode', '');
        }
    };

    const CustomOption = (props) => (
        <components.Option {...props}>
            <div>
                <strong>{props.data.feature.text}</strong>,{' '}
                {props.data.feature.place_name.split(', ').slice(1).join(', ')}
            </div>
        </components.Option>
    );

    return (
        <AsyncSelect
            cacheOptions
            loadOptions={loadOptions}
            defaultOptions
            onChange={handleChange}
            value={values.streetAddress ? { label: values.streetAddress, value: values.streetAddress } : null}
            styles={customStyles}
            placeholder="Enter address"
            className="multi-select-field"
            classNamePrefix="__select-days"
            components={{ Option: CustomOption }}
            isClearable
            onBlur={() => {}}
        />
    );
};

GroupAddressAutocomplete.propTypes = {
    setFieldValue: PropTypes.func.isRequired,
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired
};

export default GroupAddressAutocomplete;
